/**
 * theme.js — Tenant theme → CSS custom properties.
 *
 * PURPOSE:
 *   Mirrors the canonical `theme.*` tenant-config keys onto --ws-* custom
 *   properties on a shadow host. Lifted out of init.js so BOTH shadow roots
 *   the widget owns can be themed from one place:
 *     - the main widget root (launcher + chat panel), and
 *     - the host-page search bar's own root (features/optional/search-bar.js).
 *
 *   Keep this the ONLY place that maps theme keys → CSS vars. styles/variables.css
 *   holds the matching neutral defaults; update both together.
 */

import { getTheme } from './configAccessors.js';

// Mirror canonical theme.* keys onto CSS custom properties.
// Note: any var set inline here overrides :host(.ws-tenant-X) rules in
// variables.css. Only inline-set the vars we want to be applyTheme-controlled.
export function applyTheme(shadow) {
  if (!shadow) return;
  const setVar = (name, value, fallback) => {
    const v = value == null || value === '' ? fallback : value;
    if (v != null) shadow.host.style.setProperty(name, v);
  };
  setVar('--ws-primary',              getTheme('primary_color'),         '#2C2C2C');
  setVar('--ws-accent',               getTheme('accent_color'),          '#8C8C8C');
  setVar('--ws-selected-chip',         getTheme('selected_chip_color'),   null);
  setVar('--ws-text',                 getTheme('text_color'),            '#1A1A1A');
  setVar('--ws-background',           getTheme('background_color'),      '#FFFFFF');
  setVar('--ws-font',                 getTheme('font_family'),           "'Open Sans', sans-serif");
  setVar('--ws-border',               getTheme('border_color'),          '#E0E0E0');
  setVar('--ws-surface',              getTheme('surface_color'),         '#F5F5F5');
  setVar('--ws-scrollbar-icon-color', getTheme('scrollbar_icon_color'),  '#574D39');
  setVar('--ws-mic-color',            getTheme('mic_color'),             '#574D39');
  setVar('--ws-header-action-color',  getTheme('header_action_color'),   '#ffffff');
  setVar('--ws-header-logo-color',    getTheme('header_logo_color'),     '#ffffff');
  // Optional: header background distinct from the primary. Unset → CSS falls
  // back to var(--ws-primary), so wineries that don't set it are unchanged.
  setVar('--ws-header-bg',            getTheme('header_background_color'), null);
  // Optional: banner promo-strip colour. A plain colour is wrapped into a flat
  // gradient (the strip's colour layer is a background-image). Unset → the CSS
  // default gold gradient (Fogarty unchanged).
  const bannerStrip = getTheme('banner_strip_color');
  if (bannerStrip) {
    shadow.host.style.setProperty('--ws-banner-strip', `linear-gradient(${bannerStrip}, ${bannerStrip})`);
  }
  setVar('--ws-header-logo-height',   getTheme('header_logo_height'),    '32px');

  // Rich-card colours (shared cards.css). A winery themes its cards from the
  // admin tenant-config theme; fallbacks are the cards.css defaults, so a winery
  // that sets none of these renders exactly as before. A winery that also
  // structurally overrides a card in its styles.css still wins over these.
  setVar('--ws-rich-accent',          getTheme('card_accent_color'),     '#972553');
  setVar('--ws-rich-cta-bg',          getTheme('card_cta_color'),        '#972553');
  setVar('--ws-rich-cta-bg-hover',    getTheme('card_cta_hover_color'),  '#A93363');
  setVar('--ws-rich-deep',            getTheme('card_deep_color'),       '#691B38');
  setVar('--ws-rich-title',           getTheme('card_title_color'),      '#1A1A1A');
  setVar('--ws-rich-on-accent',       getTheme('card_on_accent_color'),  '#FFFFFF');

  // Wine drawer — defaults inherit rich-card palette; optional drawer_* theme keys override.
  setVar('--ws-drawer-heading',         getTheme('drawer_heading_color'),     null);
  setVar('--ws-drawer-tab-active',      getTheme('drawer_tab_active_color'),  null);
  setVar('--ws-drawer-cta',             getTheme('drawer_cta_color'),         null);
  setVar('--ws-drawer-surface',         getTheme('drawer_surface_color'),     null);
}
